/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { AuthenticationService } from '@alfresco/adf-core';
import { TreeNode } from '../components/tree-nodes/tree-nodes.component';
import { AppStore } from '@alfresco/aca-shared/store';
import { Store } from '@ngrx/store';
import { algAuthorizationHeader } from '../alg-auth';

/** Taille de page des enfants. Le composant d'arborescence avance son skipCount de cette valeur. */
export const NODE_CHILDREN_PAGE_SIZE = 100;

@Injectable({
  providedIn: 'root'
})
export class NodesService {
  protected store = inject<Store<AppStore>>(Store<AppStore>);
  private readonly apiUrl = window.location.origin + '/alfresco/api/-default-/public/alfresco/versions/1';

  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthenticationService);

  /**
   * Enfants d'un noeud, pour l'arborescence — dossiers ET fichiers.
   *
   * `orderBy=isFolder DESC,name ASC` : les dossiers arrivent en premier. C'est ce qui remplace le
   * `where=(isFolder=true)` que portait cette methode tant que l'arborescence n'affichait pas les
   * fichiers. Le filtre reglait un probleme reel de pagination — sur un dossier de 500 fichiers et
   * 2 sous-dossiers, il fallait enchainer les « Charger les dossiers suivants » a travers les
   * fichiers avant de voir apparaitre le second sous-dossier. Trier les dossiers d'abord garde
   * cette garantie tout en ramenant les fichiers : la premiere page contient tous les
   * sous-dossiers, jusqu'a 100.
   *
   * `content` figure dans `fields` pour le type MIME, dont depend l'icone de fichier.
   *
   * `maxItems` explicite : le code de pagination ajoute 100 au skipCount, autant ne pas dependre
   * silencieusement de la valeur par defaut du serveur.
   */
  getNodeChildren(nodeId: string, skip: number = 0): Observable<any> {
    const endpoint = `${this.apiUrl}/nodes/${nodeId}/children`;
    let headers = new HttpHeaders();
    const auth = algAuthorizationHeader(this.authService);
    headers = headers.append('Authorization', auth);
    const params = {
      headers: headers,
      params: {
        skipCount: skip,
        maxItems: NODE_CHILDREN_PAGE_SIZE,
        orderBy: 'isFolder DESC,name ASC',
        fields: 'id,name,isFile,isFolder,parentId,content'
      }
    };
    return this.http.get<TreeNode[]>(endpoint, params);
  }

  /**
   * Un dossier a-t-il au moins un enfant ?
   *
   * `maxItems=1` et `fields=id` : la reponse ne porte quasiment que sa pagination, dont
   * `totalItems`. C'est une requete par dossier, mais minuscule, et elles partent en parallele.
   *
   * Pourquoi une requete dediee : le modele de noeud d'Alfresco ne porte aucun compteur d'enfants,
   * l'information n'existe nulle part dans la reponse du niveau parent. La requete a facettes sur
   * `PARENT`, qui aurait donne tous les comptes d'un niveau en un seul appel, a ete essayee et
   * ECARTEE : elle echoue en « Solr request failed with 500 » sur cette version.
   */
  hasChildren(nodeId: string): Observable<boolean> {
    const endpoint = `${this.apiUrl}/nodes/${nodeId}/children`;
    let headers = new HttpHeaders();
    headers = headers.append('Authorization', algAuthorizationHeader(this.authService));

    return this.http
      .get<any>(endpoint, { headers, params: { maxItems: 1, fields: 'id' } })
      .pipe(map((response) => (response?.list?.pagination?.totalItems ?? 0) > 0));
  }

  getNode(nodeId: string): Observable<any> {
    const endpoint = `${this.apiUrl}/nodes/${nodeId}`;
    let headers = new HttpHeaders();
    const auth = algAuthorizationHeader(this.authService);
    headers = headers.append('Authorization', auth);
    const params = { headers: headers };
    return this.http.get(endpoint, params);
  }
}
